import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Ambulance, Inbox, Phone } from "lucide-react";
import { Button } from "./button";
import Spinner from "./spinner";

/** 112/108 buttons for screens a bystander may see before (or instead of) the emergency info. */
export const EmergencyCallStrip = ({ className = "" }) => (
  <div className={`grid grid-cols-2 gap-2 ${className}`} role="group" aria-label="Emergency numbers">
    <a
      href="tel:112"
      className="flex min-h-12 items-center justify-center gap-2 rounded-lg bg-red-700 px-3 text-base font-bold text-white no-underline"
    >
      <Phone size={18} aria-hidden />
      Call 112
    </a>
    <a
      href="tel:108"
      className="flex min-h-12 items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 text-base font-bold text-white no-underline"
    >
      <Ambulance size={18} aria-hidden />
      Ambulance 108
    </a>
  </div>
);

export const LoadingState = ({ label = "Loading...", className = "", emergencyCalls = false }) => (
  <div className={`min-h-screen bg-slate-50 flex items-center justify-center p-4 ${className}`}>
    <div className="bg-white rounded-lg border border-slate-200 p-8 w-full max-w-sm text-center shadow-sm">
      <Spinner label={label} />
      {emergencyCalls && (
        <>
          <p className="mt-5 mb-2 text-sm text-slate-600">In an emergency, don't wait — call now:</p>
          <EmergencyCallStrip />
        </>
      )}
    </div>
  </div>
);

export const ErrorState = ({
  title = "Something went wrong",
  message,
  onRetry,
  retryLabel = "Try again",
  showHome = true,
  homeLabel = "Go to home page",
  emergencyCalls = false,
  children,
}) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-lg border border-red-200 p-6 w-full max-w-sm text-center shadow-sm">
      <AlertTriangle size={40} className="text-red-700 mx-auto mb-3" />
      <h1 className="text-lg font-bold text-slate-900 mb-2">{title}</h1>
      {message && <p className="text-base text-slate-700 mb-4">{message}</p>}
      {emergencyCalls && (
        <div className="mb-4">
          <p className="mb-2 text-sm text-slate-600">If someone needs help right now:</p>
          <EmergencyCallStrip />
        </div>
      )}
      {onRetry && (
        <Button variant="primary" fullWidth onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
      {showHome && (
        <Link to="/" className="inline-block mt-3 text-base font-semibold text-primary">
          {homeLabel}
        </Link>
      )}
      {children}
    </div>
  </div>
);

export const EmptyState = ({ title, message, action }) => (
  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
    <Inbox size={48} className="text-slate-300 mb-4" />
    <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
    {message && <p className="text-base text-slate-600 mb-4 max-w-xs">{message}</p>}
    {action}
  </div>
);
